import { hasCoverageEligible } from 'core/helpers/quoter.service';

/**
 * Return the policy whose term is in force on `targetDate`.
 *
 * This picks across separate policies that each represent a different term (segments WITHIN the
 * selected policy are always all merged). A bundled account can carry the current auto term and
 * the upcoming renewal term as two distinct policies (e.g. AUTO-002 active + AUTO-003 future), so
 * umbrella validation on a future-effective home must compare against the auto policy covering the same
 * date, not just whichever auto policy happens to be first in the list.
 *
 * A policy covers `targetDate` when effectiveDate <= targetDate < endDate (an absent endDate is treated
 * as open-ended). With no `targetDate` we keep the prior behavior of returning the first policy. When no
 * policy covers the date we fall back to the latest policy that has already started, and if the date
 * precedes every term, to the first policy.
 */
export const getPolicyInForceOn = (policies, targetDate) => {
  if (!policies?.length) {
    return undefined;
  }
  if (!targetDate) {
    return policies[0];
  }
  const target = new Date(targetDate);
  const covering = policies.find((policy) => {
    if (!policy.effectiveDate) {
      return false;
    }
    const effective = new Date(policy.effectiveDate);
    const end = policy.endDate ? new Date(policy.endDate) : null;
    return effective <= target && (!end || target < end);
  });
  if (covering) {
    return covering;
  }
  const started = policies
    .filter((policy) => policy.effectiveDate && new Date(policy.effectiveDate) <= target)
    .sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate));
  return started.length ? started[started.length - 1] : policies[0];
};

/**
 * Return every policy whose term overlaps the window [startDate, endDate). Used by umbrella
 * validation: a home term can span two 6-month auto terms, and per UW an umbrella add must
 * validate underlying limits on every ALREADY-CREATED auto term underneath it — but only on
 * terms that exist (a not-yet-generated renewal can't be validated, so it simply isn't in the
 * list). Ordered by effectiveDate. Returns [] when either bound is missing so callers can fall
 * back to single-term selection.
 */
export const getPoliciesInForceDuring = (policies, startDate, endDate) => {
  if (!policies?.length || !startDate || !endDate) {
    return [];
  }
  const windowStart = new Date(startDate);
  const windowEnd = new Date(endDate);
  return policies
    .filter((policy) => {
      if (!policy.effectiveDate) {
        return false;
      }
      const effective = new Date(policy.effectiveDate);
      const end = policy.endDate ? new Date(policy.endDate) : null;
      return effective < windowEnd && (!end || end > windowStart);
    })
    .sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate));
};

/**
 * Compose the weakest autoCoverage across merged auto policy details, PER FIELD: umbrella
 * eligibility must hold on every underlying auto term, and the BI gate (canAddUmbrella) and the
 * excess-UM/UIM gate (canAddUmbrellaUMUIM) are independent — the term with the weakest BI is not
 * necessarily the term with the weakest UM/UIM, so swapping in one term's whole object could let
 * another term's failing field slip past its gate. BI picks the lowest per-occurrence value (the
 * gate is a >= threshold, so min is threshold-independent); UM/UIM pick any value that would fail
 * hasCoverageEligible's fixed 250/500 (or 500 CSL) floors, else the in-force term's value. All
 * other fields come from the in-force term (mergedDetailsList[0]). A term with no autoCoverage at
 * all returns undefined (canAddUmbrella rejects an unverifiable limit — the conservative outcome).
 */
export const pickWeakestAutoCoverage = (mergedDetailsList) => {
  const coverages = mergedDetailsList.map((details) => details?.autoCoverage);
  if (coverages.some((coverage) => !coverage)) {
    return undefined;
  }

  const occurrenceLimit = (limitBIPD) => {
    if (!limitBIPD || typeof limitBIPD !== 'string') {
      return Number.NEGATIVE_INFINITY;
    }
    const parsed = limitBIPD.includes('CSL')
      ? Number.parseInt(limitBIPD.split(' ')[0], 10)
      : Number.parseInt(limitBIPD.split('/')[1], 10);
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
  };
  const umuimEligible = (limit) => {
    if (!limit || typeof limit !== 'string') {
      return false;
    }
    // same split as canHaveExcessUM in quoter.service.js
    const parts = limit.includes('/') ? limit.split('/') : limit.split(' ').slice(0, 1);
    return hasCoverageEligible(parts);
  };

  const weakestBIPD = coverages.reduce((weakest, { policyLimitBIPD }) =>
    occurrenceLimit(policyLimitBIPD) < occurrenceLimit(weakest.policyLimitBIPD) ? { policyLimitBIPD } : weakest
  ).policyLimitBIPD;
  const failingUMBI = coverages.find((coverage) => !umuimEligible(coverage.policyLimitUMBI));
  const failingUIMBI = coverages.find((coverage) => !umuimEligible(coverage.policyLimitUIMBI));

  return {
    ...coverages[0],
    policyLimitBIPD: weakestBIPD,
    policyLimitUMBI: failingUMBI ? failingUMBI.policyLimitUMBI : coverages[0].policyLimitUMBI,
    policyLimitUIMBI: failingUIMBI ? failingUIMBI.policyLimitUIMBI : coverages[0].policyLimitUIMBI
  };
};
